// @ts-nocheck
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';

import useRedirectAfterLogin from './hooks/useRedirectAfterLogin';
import AppLayout from './layouts/AppLayout';
import AuthLayout from './layouts/AuthLayout';
import SetupLayout from './layouts/SetupLayout';
import HubSpotSSORedirect, { saveUrlToRedirectHubSpot } from './libs/HubSpotSSORedirect';
import ErrorPage from './pages/ErrorPage';
import LoadingPage from './pages/LoadingPage';
import FeatureFlagsProvider from './providers/FeatureFlagsProvider';
import PortalProvider from './providers/PortalProvider';
import PrivateRouterProvider from './providers/PrivateRouterProvider';
import PublicRouterProvider from './providers/PublicRouterProvider';
import routes, { type IRoute } from './routes';
import type { AppState } from './store';

import { Location } from '@cobliteam/common-types';

import { isWebDataFetching } from '@cobliteam/api-lib';
import { AuthToken } from '@cobliteam/api-lib/src/account/session';

import { lazy } from '@cobliteam/lazy-with-retries';

import PrivateRoute from './components/PrivateRoute';
import UpdateBanner from './components/UpdateBanner';

const Login = lazy(() => import('@cobliteam/login/src/pages/Login'));
const PasswordRecovery = lazy(() => import('@cobliteam/login/src/pages/PasswordRecovery'));
const NoFleetAccess = lazy(() => import('@cobliteam/login/src/pages/NoFleetAccess'));
const TokenValidation = lazy(() => import('@cobliteam/login/src/pages/TokenValidation'));
const PasswordReset = lazy(() => import('@cobliteam/login/src/pages/PasswordReset'));
const OnboardingPage = lazy(() => import('@cobliteam/onboarding'));

// Auth Routes
const Auth = () => (
  <Route exact path={[Location.LOGIN, Location.PASSWORD_RECOVERY, Location.TOKEN_VALIDATION, Location.PASSWORD_RESET]}>
    <AuthLayout>
      <Route exact path={Location.LOGIN} component={Login} />
      <Route exact path={Location.PASSWORD_RECOVERY} component={PasswordRecovery} />
      <Route exact path={Location.TOKEN_VALIDATION} component={TokenValidation} />
      <Route exact path={Location.PASSWORD_RESET} component={PasswordReset} />
    </AuthLayout>
  </Route>
);

// 🌐 Public Routes
const Public = () => (
  <PublicRouterProvider>
    <Route exact path={Location.NO_FLEET_ACCESS} component={NoFleetAccess} />
    <Auth />
  </PublicRouterProvider>
);

// 🔐 Private Routes
const Private = () => {
  return (
    <PrivateRouterProvider>
      <HubSpotSSORedirect>
        <FeatureFlagsProvider>
          <AppLayout>
            <UpdateBanner />
            <PortalProvider>
              {routes.map((route: IRoute) => (
                <ErrorBoundary
                  key={route.path}
                  FallbackComponent={({ error, resetErrorBoundary }) => <ErrorPage path={route.path} error={error} resetErrorBoundary={resetErrorBoundary} />}
                >
                  <PrivateRoute key={route.path} route={route} />
                </ErrorBoundary>
              ))}
            </PortalProvider>
          </AppLayout>
          <SetupLayout>
            <Route path={Location.ONBOARDING_SETUP} component={OnboardingPage} />
          </SetupLayout>
        </FeatureFlagsProvider>
      </HubSpotSSORedirect>
    </PrivateRouterProvider>
  );
};

//  🔂 Redirect /# -> /
const RedirectHash = () => {
  const authToken = localStorage.getItem(AuthToken.ACCESS_TOKEN);
  return (
    <Route
      path='/'
      render={({ location }) => {
        if (!location.hash.includes('#')) return null;
        if (!authToken) return <Redirect to={Location.LOGIN} />;
        return <Redirect to={location.hash.replace('#', '')} />;
      }}
    />
  );
};

const Router = () => {
  const loginWebData = useSelector((state: AppState) => state.login.loginWebData);
  useRedirectAfterLogin();
  saveUrlToRedirectHubSpot();

  if (isWebDataFetching(loginWebData)) return <LoadingPage message='Carregando dados de acesso...' />;

  return (
    <Switch>
      <>
        <Public />
        <Private />
        <RedirectHash />
      </>
    </Switch>
  );
};

export default Router;
